import * as api from "@/api/hitachi/rbi";
import { addDefectAttachment } from "@/api/welds";
import { getHitachiInventory } from "@/api/hitachi/inventory";
import { InspectorContext } from "./reducer";
import { InspectorRow, WeldListFilters } from "@/api/hitachi/rbi";
import { Inventory, InventoryMap } from "./types";
import { Page } from "@/util";
import { useContext } from "react";
import { useMutation, useQuery } from "react-query";

export function useGetOperations(subProjectId?: string, ndtDiscipline?: string) {
  const unitId = subProjectId ?? "";
  const discipline = ndtDiscipline ?? "";

  return useQuery({
    enabled: Boolean(unitId) && Boolean(discipline),
    queryKey: ["rbi/inspector/operations", { unitId, discipline }],
    queryFn: () => api.getInspectorOperations(unitId, discipline),
    select: (data: string[]) => {
      return data.map((fullString) => ({
        withDescription: fullString,
        number: fullString.split(" ")[0],
      }));
    },
  });
}

export function useGetWeldList(filters: Partial<WeldListFilters>) {
  return useQuery({
    queryKey: ["rbi/inspector/welds", filters],
    queryFn: () => api.getInspectorWeldList(filters),
  });
}

export function useGetInspections(discipline: string, weldIds: string[]) {
  const { updateState } = useContext(InspectorContext);
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["rbi/welder/inspections", { discipline, weldIds }],
    queryFn: () => api.getInspectorRows(discipline, weldIds),
    onSuccess: (data) => {
      updateState({ type: "Inspections", payload: data });
    },
  });
}

export function useInventory(inventory: Inventory) {
  const inventoryId = InventoryMap[inventory];
  return useQuery({
    queryKey: ["inventory", inventoryId],
    queryFn: () => getHitachiInventory(inventoryId),
  });
}

export function useSave() {
  return useMutation(async (args: { inspections: InspectorRow[]; redirect: boolean }) => {
    await api.saveInspectorRows(args.inspections);
    await saveAttachments(args.inspections);
    if (args.redirect) {
      Page.redirect("rbi/hitachi/inspector");
    }
  });
}

function saveAttachments(inspections: InspectorRow[]) {
  return Promise.all(
    inspections.map(async (row) => {
      if (row.defects.length > 0) {
        row.defects.map(async (defect) => {
          if (defect.attachment) {
            await addDefectAttachment(defect.defectId, defect.attachment);
          }
        });
      }
    })
  );
}

export function useQualityPlanLocation(subProjectId?: string) {
  const unitId = subProjectId ?? "";
  return useQuery({
    enabled: Boolean(unitId),
    queryKey: ["rbi/hitachi/quality-plan/location", { unitId }],
    queryFn: () => api.getQualityPlanLocation(unitId),
  });
}
